import Button from '@atoms/Button/Button';
import Modal from '@atoms/Modal/Modal';
import { ILink } from '@models/ILink';
import { IMessage } from '@models/IMessage';
import { IPricingData } from '@models/IPricingData';
import { IProductListingStatusLabels } from '@models/IProductListingStatusLabels';
import { ISignupForm } from '@models/ISignupForm';
import ErrorBannerNotificationEnum from '@models/enums/ErrorBannerNotificationEnum';
import InfoTypeEnum from '@models/enums/InfoTypeEnum';
import ProductStockStatusEnum from '@models/enums/ProductStockStatusEnum';
import InfoModal from '@molecules/InfoModal/InfoModal';
import SignupForm from '@organisms/SignupForm/SignupForm';
import signupFormGetProps from '@services/signupFormGetProps';
import useErrorNotificationStore from '@state/ErrorBannerNotificationDisplayStore';
import updateDataLayer from '@utils/helpers/gtm';
import { useState } from 'react';

interface Props {
  readonly backOrderText: IMessage;
  readonly preOrderText: IMessage;
  readonly disableFindDealerCta?: boolean;
  readonly dealerFindLink: ILink;
  readonly limitedDisplay: boolean;
  readonly notifyMeContactFormEndpoint: string;
  readonly pricingData: IPricingData;
  readonly skuCode: string;
  readonly statusLabels: IProductListingStatusLabels;
  readonly addToCartHandler: () => Promise<void>;
  readonly buyNowLabel?: string;
  readonly buyNowUrl?: string;
  readonly dataTestId?: string;
}

const ProductDetailsCta = ({
  backOrderText,
  preOrderText,
  disableFindDealerCta,
  dealerFindLink,
  limitedDisplay,
  notifyMeContactFormEndpoint,
  pricingData,
  skuCode,
  statusLabels,
  addToCartHandler,
  buyNowLabel,
  buyNowUrl,
  dataTestId = 'Product-Details-CTA',
}: Props) => {
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [infoModalContent, setInfoModalContent] = useState<IMessage>({
    title: '',
    text: '',
  });

  const [isSignupFormOpen, setIsSignupFormOpen] = useState(false);
  const [signupFormData, setSignupFormData] = useState<ISignupForm>();

  const handleInfoModal = (content: IMessage) => {
    setInfoModalContent(content);
    setIsInfoModalOpen(true);
  };

  const { setErrorBannerNotification } = useErrorNotificationStore();

  const handleFindDealerClick = () => {
    updateDataLayer({
      event: 'intentToBuy',
      content_type: dealerFindLink?.name || 'Find dealer',
      items: [productData],
    });
  };

  const notifyMeHandler = async () => {
    try {
      const signupFormResponse = await signupFormGetProps(
        notifyMeContactFormEndpoint
      );

      setSignupFormData(signupFormResponse);
      setIsSignupFormOpen(true);
    } catch (error) {
      setErrorBannerNotification(
        ErrorBannerNotificationEnum.ProductDetailsNotifyMe
      );
    }
  };

  const handleModalAddToCartClick = async () => {
    await addToCartHandler();
    setIsInfoModalOpen(false);
  };

  return (
    <div
      className="flex flex-col flex-wrap items-start justify-start gap-5 xl:flex-row"
      data-testid={dataTestId}
    >
      {pricingData?.status === ProductStockStatusEnum.InStock && (
        <>
          {!!pricingData?.price && (
            <Button
              onClick={addToCartHandler}
              dataTestId={`${dataTestId}-Add-To-Cart`}
            >
              {statusLabels.addToCartCTA}
            </Button>
          )}
          {!disableFindDealerCta &&
            dealerFindLink?.name &&
            dealerFindLink?.url && (
              <Button
                data-testid="find-dealer-button"
                secondary
                link={dealerFindLink}
                linkCallback={handleFindDealerClick}
                dataTestId={`${dataTestId}-Find-Dealer`}
              />
            )}
        </>
      )}

      {buyNowLabel && buyNowUrl && (
        <Button
          link={{ url: buyNowUrl, name: buyNowLabel }}
          dataTestId={`${dataTestId}-Buy-Now`}
        />
      )}

      {(pricingData?.status === ProductStockStatusEnum.PreOrder ||
        pricingData?.status === ProductStockStatusEnum.BackOrder) && (
        <>
          {!!pricingData?.price && (
            <Button
              onClick={() =>
                handleInfoModal(
                  pricingData.status === ProductStockStatusEnum.PreOrder
                    ? preOrderText
                    : backOrderText
                )
              }
              dataTestId={`${dataTestId}-PreOrder/BackOrder`}
            >
              {statusLabels.addToCartCTA}
            </Button>
          )}

          {!disableFindDealerCta &&
            dealerFindLink?.name &&
            dealerFindLink?.url && (
              <Button
                data-testid="find-dealer-button"
                secondary
                link={dealerFindLink}
                linkCallback={handleFindDealerClick}
                dataTestId={`${dataTestId}-Find-Dealer`}
              />
            )}

          {!!pricingData?.price && (
            <Button
              onClick={notifyMeHandler}
              dataTestId={`${dataTestId}-Notifications-Signup`}
            >
              {statusLabels.signUpForNotificationsCTA}
            </Button>
          )}
        </>
      )}

      {(pricingData?.status === ProductStockStatusEnum.OutOfStock ||
        pricingData?.status === ProductStockStatusEnum.ComingSoon) && (
        <>
          {!!pricingData?.price && (
            <Button
              onClick={notifyMeHandler}
              dataTestId={`${dataTestId}-Notifications-Signup`}
            >
              {statusLabels.signUpForNotificationsCTA}
            </Button>
          )}
          {!disableFindDealerCta &&
            dealerFindLink?.name &&
            dealerFindLink?.url && (
              <Button
                secondary
                link={dealerFindLink}
                linkCallback={handleFindDealerClick}
                dataTestId={`${dataTestId}-Find-Dealer`}
              />
            )}
        </>
      )}

      {(pricingData?.status === ProductStockStatusEnum.SoldOut ||
        limitedDisplay) &&
        dealerFindLink?.name &&
        dealerFindLink?.url && (
          <Button
            link={dealerFindLink}
            linkCallback={handleFindDealerClick}
            secondary={!!(buyNowLabel && buyNowUrl)}
            dataTestId={`${dataTestId}-Find-Dealer`}
          />
        )}

      <InfoModal
        type={InfoTypeEnum.Information}
        isModalOpen={isInfoModalOpen}
        toggleModal={setIsInfoModalOpen}
        title={infoModalContent.title}
        description={infoModalContent.text}
        buttonText={statusLabels.addToCartCTA}
        handleCTAClick={handleModalAddToCartClick}
        dataTestId={`${dataTestId}-Info-Modal`}
      />

      <Modal
        isOpen={isSignupFormOpen}
        setIsOpen={setIsSignupFormOpen}
        showCloseButton={false}
        transparent
        contentClasses="py-4"
      >
        {signupFormData && (
          <SignupForm
            {...signupFormData}
            productCode={skuCode}
            closeCallback={() => setIsSignupFormOpen(!isSignupFormOpen)}
          />
        )}
      </Modal>
    </div>
  );
};

export default ProductDetailsCta;
